/**
 * Login Page
 *
 * Allows existing users to log into the system.
 * On successful login, redirects to the dashboard.
 *
 * Features:
 * - Email/password form
 * - Form validation
 * - Error handling
 * - Link to registration
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../context/AuthContext';
import Input from '../components/common/Input';
import Button from '../components/common/Button';

/**
 * Login Component
 *
 * Renders the login form and handles authentication.
 */
function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  // Form state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // React Hook Form setup
  const { register, handleSubmit, formState: { errors } } = useForm();

  /**
   * Handle form submission
   * @param {Object} data - Form data { email, password }
   */
  const onSubmit = async (data) => {
    setIsLoading(true);
    setError(null);

    try {
      // Call login from auth context
      await login(data.email, data.password);

      // Redirect to dashboard on success
      navigate('/');
    } catch (err) {
      // Show error message
      setError(err.message || 'Failed to log in. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center">
            <span className="text-white text-2xl font-bold">OMS</span>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            Sign in to your account
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Or{' '}
            <Link to="/register" className="text-blue-600 hover:text-blue-500">
              create a new account
            </Link>
          </p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <svg
                className="h-5 w-5 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="ml-2 text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="bg-white p-8 rounded-lg shadow-md">
          <div className="space-y-4">
            {/* Email */}
            <Input
              label="Email address"
              type="email"
              autoComplete="email"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
              error={errors.email}
              placeholder=""
            />

            {/* Password */}
            <Input
              label="Password"
              type="password"
              autoComplete="current-password"
              {...register('password', {
                required: 'Password is required'
              })}
              error={errors.password}
              placeholder=""
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full mt-6"
            size="lg"
            loading={isLoading}
          >
            Sign in
          </Button>
        </form>
      </div>
    </div>
  );
}

export default Login;
